/**
 * Cloudflare Turnstile service for CAPTCHA verification
 */

import { Log } from '@ainova/core';

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}

class TurnstileService {
  private logger: Log;
  private siteKey: string;
  private enabled: boolean;
  private scriptLoaded = false;
  private scriptLoadPromise: Promise<void> | null = null;

  /**
   * One challenge at a time.
   *
   * Every getToken() renders its own invisible widget, and the app asks for
   * tokens in bursts — uploading a list refreshes the search behind it, so two
   * challenges can be in flight on the same page for the same site key within a
   * few milliseconds. Cloudflare answers the second with 401, which surfaces
   * here as no token at all and at the API as "verification token is required".
   *
   * Queued, not shared: a token is single-use, so two callers need two of them.
   * This only stops the widgets overlapping.
   */
  private chain: Promise<unknown> = Promise.resolve();

  constructor() {
    this.logger = new Log('Turnstile');
    this.siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
    this.enabled = import.meta.env.VITE_TURNSTILE_ENABLED !== 'false';

    if (!this.enabled) {
      this.logger.info('Turnstile disabled');
    } else if (!this.siteKey) {
      this.logger.warn('Turnstile site key not configured');
    }
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window object not available'));
        return;
      }

      // Check if script already loaded
      if (window.turnstile) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.scriptLoaded = true;
        this.logger.info('Turnstile script loaded');
        resolve();
      };

      script.onerror = () => {
        this.logger.error('Failed to load Turnstile script');
        reject(new Error('Failed to load Turnstile script'));
      };

      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  /**
   * A token, with the challenges queued behind one another.
   *
   * The queue link is attached whatever the outcome, so one failed challenge
   * does not wedge every later one behind a rejected promise.
   */
  async getToken(): Promise<string> {
    const mine = this.chain.then(
      () => this.challenge(),
      () => this.challenge()
    );
    this.chain = mine.catch(() => undefined);
    return mine;
  }

  private async challenge(): Promise<string> {
    if (!this.enabled) {
      this.logger.info('Turnstile disabled, returning empty token');
      return '';
    }

    if (!this.siteKey) {
      this.logger.warn('Turnstile site key not configured, returning empty token');
      return '';
    }

    try {
      await this.loadScript();

      // Awaited, not returned: a returned promise rejects past this try, so a
      // challenge Cloudflare refused — a 401, an expired widget — escaped as an
      // exception while the two branches above and the catch below all answer
      // with an empty token. Every caller wrapped it in its own try as a
      // result. One contract now: this never throws, and an empty token means
      // the service decides.
      return await new Promise<string>((resolve, reject) => {
        if (!window.turnstile) {
          reject(new Error('Turnstile not available'));
          return;
        }

        // Create a temporary container
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.top = '-9999px';
        container.style.left = '-9999px';
        document.body.appendChild(container);

        try {
          const widgetId = window.turnstile.render(container, {
            sitekey: this.siteKey,
            size: 'invisible',
            theme: 'light',
            callback: (token: string) => {
              // Clean up
              if (window.turnstile) {
                window.turnstile.remove(widgetId);
              }
              container.remove();
              resolve(token);
            },
            'error-callback': () => {
              // Clean up
              if (window.turnstile) {
                window.turnstile.remove(widgetId);
              }
              container.remove();
              reject(new Error('Turnstile verification failed'));
            },
            'timeout-callback': () => {
              // Clean up on timeout
              if (window.turnstile) {
                window.turnstile.remove(widgetId);
              }
              container.remove();
              reject(new Error('Turnstile verification timed out'));
            }
          });
        } catch (error) {
          container.remove();
          reject(error);
        }
      });
    } catch (error) {
      this.logger.error('Failed to get Turnstile token', error);
      return '';
    }
  }
}

export const turnstile = new TurnstileService();

/**
 * The token to send with a call to a guarded route, or undefined.
 *
 * Every guarded caller wants the same three things and had been writing them
 * out separately: ask for a token, give up after a moment, and send the request
 * either way because the service is what decides whether a token is required.
 * Written out per call site it is five copies of the same eight lines, two of
 * which drifted — `/deep-iq/assistant` and `/deep-iq/filter-companies` were
 * written without any of it, so every assistant message and every filter over a
 * mixed result set reached the API with no token and was refused.
 *
 * The timeout matters as much as the token. getToken() resolves when Cloudflare
 * answers, and an invisible challenge that never calls back never rejects — so
 * without a deadline a customer waits on a spinner for a widget they cannot
 * see. Five seconds is what the filter search and the title suggestions already
 * used.
 */
export async function guardToken(timeoutMs = 5000): Promise<string | undefined> {
  try {
    return await Promise.race([
      turnstile.getToken(),
      new Promise<string>((_, reject) =>
        setTimeout(() => reject(new Error('Turnstile timeout')), timeoutMs)
      )
    ]);
  } catch {
    // Deliberately not fatal: the route decides. A disabled or unconfigured
    // widget already returns an empty string rather than throwing, and this
    // keeps a Turnstile outage from taking the app down with it.
    return undefined;
  }
}
